/**
 * Collection Filtering System
 * Provides smart filtering for collection pages based on product tags and taxonomy data
 * Supports hierarchical URLs and SEO-friendly filtering
 */



class CollectionFilters {
  constructor() {
    this.container = document.querySelector('.collection-filters') || document.querySelector('.collection-cross-refs');
    this.productGrid = document.querySelector('.collection-products') || document.querySelector('.collection-grid');
    this.resultsCount = document.querySelector('.collection-results-count');
    this.breadcrumbs = document.querySelector('.collection-breadcrumbs');

    this.activeFilters = new Map();
    this.allProducts = [];
    this.filteredProducts = [];
    this.taxonomyData = null;

    // Pagination properties
    this.currentPage = 1;
    this.productsPerPage = 20;
    this.totalPages = 1;

    // Search functionality
    this.searchInput = null;
    this.searchClearBtn = null;
    this.searchQuery = '';
    this.searchDebounceTimer = null;

    this.init();
  }

  async init() {
    console.log('CollectionFilters init - container:', this.container);
    console.log('CollectionFilters init - productGrid:', this.productGrid);

    if (!this.container) {
      console.log('No filter container found');
      return;
    }

    if (!this.productGrid) {
      console.log('No product grid found');
      return;
    }

    this.loadTaxonomyData();

    // Only load all products if this is a taxonomy-enabled collection
    if (this.hasTaxonomyData()) {
      await this.loadProducts(); // Load all products for filtering
      console.log('Taxonomy collection: loaded all products for filtering');
    } else {
      console.log('Regular collection: using pagination');
    }

    // Only build filter UI for taxonomy collections
    if (this.hasTaxonomyData()) {
      document.body.classList.add('has-taxonomy');
      this.buildFilterUI();
      this.buildCrossReferences();
      this.bindEvents();
      this.parseURLFilters();
      this.parseURLPage();
      this.applyFilters(false); // Don't reset page on initial load
      this.setupTaxonomySorting(); // Client-side sorting for filtered results
      console.log('Taxonomy collection: filter UI enabled');

      // Debug: Check for empty elements in sidebar
      setTimeout(() => {
        const sidebar = document.querySelector('.collection-sidebar');
        if (sidebar) {
          const children = Array.from(sidebar.children);
          console.log('Sidebar children:', children.length);
          children.forEach((child, index) => {
            const rect = child.getBoundingClientRect();
            console.log(`Child ${index}:`, child.className, `Height: ${rect.height}px`, `Content: "${child.textContent.trim().substring(0, 50)}"`);
          });
        }
      }, 1000);
    } else {
      console.log('Regular collection: no filtering UI needed');
      this.preserveNativeSorting(); // Server-side sorting for regular collections

      // For regular collections, load products from DOM for search functionality
      this.loadProductsFromDOM();
    }

    // Initialize search functionality for all collections
    this.initializeSearch();
  }

  loadTaxonomyData() {
    const taxonomyScript = document.querySelector('.collection-taxonomy-data');
    if (taxonomyScript) {
      try {
        this.taxonomyData = JSON.parse(taxonomyScript.textContent);
        console.log('Taxonomy data loaded for filtering');
      } catch (error) {
        console.warn('Error parsing taxonomy data:', error);
      }
    }
  }

  hasTaxonomyData() {
    return this.taxonomyData &&
           this.taxonomyData.filtering &&
           this.taxonomyData.filtering.enabled;
  }



  preserveNativeSorting() {
    // Find sort controls and make sure they use native Shopify behavior
    const sortSelect = document.querySelector('select[name="sort_by"], .sort-select, #sort-by');
    if (sortSelect) {
      console.log('Found sort control, ensuring native behavior');

      // Set the dropdown to match the current URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const currentSort = urlParams.get('sort_by');

      if (currentSort) {
        sortSelect.value = currentSort;
        console.log('Set sort dropdown to:', currentSort);
      } else {
        // No sort_by parameter means "Featured" sorting
        sortSelect.value = 'manual'; // or whatever value represents "Featured"
        console.log('Set sort dropdown to: Featured (no sort_by parameter)');
      }

      // Remove any event listeners our system might have added
      const newSortSelect = sortSelect.cloneNode(true);
      sortSelect.parentNode.replaceChild(newSortSelect, sortSelect);

      // Restore the correct value after cloning
      if (currentSort) {
        newSortSelect.value = currentSort;
      } else {
        newSortSelect.value = 'manual'; // Featured sorting
      }

      // Add native Shopify sort behavior
      newSortSelect.addEventListener('change', function() {
        const url = new URL(window.location);

        // Handle "Featured" sorting - remove sort_by parameter entirely
        if (this.value === 'manual' || this.value === 'featured' || this.value === '') {
          url.searchParams.delete('sort_by');
        } else {
          url.searchParams.set('sort_by', this.value);
        }

        window.location.href = url.toString();
      });
    }
  }



  // Helper function to determine if a product should be skipped
  shouldSkipProduct(productData) {
    // Skip if no images
    if (!productData.images || productData.images.length === 0) {
      return true;
    }

    // Skip if title contains "Optional" (case-insensitive)
    if (productData.title && productData.title.toLowerCase().includes('optional')) {
      return true;
    }

    return false;
  }

  async loadProducts() {
    // First, load products from current page
    const productElements = this.productGrid.querySelectorAll('.product-item, .product-card');

    productElements.forEach(element => {
      const productData = this.extractProductData(element);
      if (productData) {
        // Filter out products with missing images or "Optional" in the title
        if (this.shouldSkipProduct(productData)) {
          return; // Skip this product
        }
        this.allProducts.push(productData);
      }
    });

    console.log(`Loaded ${this.allProducts.length} products from current page`);

    // Then try to load all products from the collection via AJAX
    try {
      await this.loadAllCollectionProducts();
      console.log(`Successfully loaded ${this.allProducts.length} total products via API`);
    } catch (error) {
      console.warn('API failed, using page-only products for filtering/sorting:', error.message);
      // When API fails, we can still do client-side operations on the current page products
      console.log(`Fallback: Using ${this.allProducts.length} products from current page`);
    }

    this.filteredProducts = [...this.allProducts];
    console.log(`Final: ${this.allProducts.length} total products available for filtering/sorting`);
  }

  async loadAllCollectionProducts() {
    // Get current collection handle from URL
    const collectionHandle = this.getCurrentCollectionHandle();
    if (!collectionHandle) {
      console.warn('Could not determine collection handle');
      throw new Error('Could not determine collection handle');
    }

    // Store page products as backup
    const pageProducts = [...this.allProducts];

    // Clear existing products and rebuild from API data
    this.allProducts = [];

    // Fetch all products using pagination
    let page = 1;
    let hasMoreProducts = true;

    while (hasMoreProducts) {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${page}`);
      if (!response.ok) {
        // Restore page products on API failure
        this.allProducts = pageProducts;
        throw new Error(`HTTP error! status: ${response.status} (${response.statusText})`);
      }

      const data = await response.json();

      if (data.products.length === 0) {
        hasMoreProducts = false;
      } else {
        // Convert API products to our format, filtering out "(Optional)" products
        data.products.forEach(product => {
          // Filter out products with "(Optional)" in the title - these are hidden Bold Product Options products
          if (product.title && product.title.includes('(Optional)')) {
            return; // Skip this product
          }
          const productData = this.convertAPIProductToData(product);
          this.allProducts.push(productData);
        });

        // If we got less than 250 products, we're on the last page
        if (data.products.length < 250) {
          hasMoreProducts = false;
        } else {
          page++;
        }
      }
    }

    console.log('Total products loaded from API:', this.allProducts.length);

    // Re-render the product grid with all products
    this.renderAllProducts();
  }

  extractProductData(element) {
    const productScript = element.querySelector('.product-data');
    if (!productScript) return null;

    try {
      const data = JSON.parse(productScript.textContent);
      data.element = element;
      return data;
    } catch (error) {
      console.warn('Error parsing product data:', error);
      return null;
    }
  }

  convertAPIProductToData(apiProduct) {
    const normalizedTags = Array.isArray(apiProduct.tags)
      ? apiProduct.tags
      : String(apiProduct.tags || '')
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0);

    // Convert Shopify API product to our internal format
    // Note: API prices are in dollars (e.g., "12.99"), but Liquid template prices are in cents (e.g., 1299)
    return {
      id: apiProduct.id,
      title: apiProduct.title,
      handle: apiProduct.handle,
      price: apiProduct.variants[0]?.price ? Math.round(parseFloat(apiProduct.variants[0].price) * 100) : 0,
      compare_at_price: apiProduct.variants[0]?.compare_at_price ? Math.round(parseFloat(apiProduct.variants[0].compare_at_price) * 100) : null,
      available: apiProduct.available || (apiProduct.variants && apiProduct.variants.some(variant => variant.available)),
      tags: normalizedTags,
      images: apiProduct.images || [],
      vendor: apiProduct.vendor,
      product_type: apiProduct.product_type,
      created_at: apiProduct.created_at,
      element: null // Will be created in renderAllProducts
    };
  }

  renderAllProducts() {
    // Clear existing product grid
    this.productGrid.innerHTML = '';

    // Create product elements for all products
    this.allProducts.forEach(product => {
      const productElement = this.createProductElement(product);
      product.element = productElement;
      this.productGrid.appendChild(productElement);
    });

    console.log('Rendered all products to grid:', this.allProducts.length);
  }

  formatMoney(cents) {
    // Convert cents to dollars and format with commas
    const dollars = (cents / 100).toFixed(2);
    const parts = dollars.split('.');
    const wholeDollars = parts[0];
    const centsStr = parts[1];

    // Add thousand separators
    const formattedDollars = wholeDollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `$${formattedDollars}.${centsStr}`;
  }

  createProductElement(product) {
    // Create a basic product card element (keep original structure)
    const productCard = document.createElement('div');
    productCard.className = 'product-card-unified product-card-unified--collection';
    productCard.setAttribute('data-product-id', product.id);

    const imageUrl = product.images[0]?.src || '';
    const formattedPrice = this.formatMoney(product.price);
    const comparePrice = product.compare_at_price ? this.formatMoney(product.compare_at_price) : null;
    const productUrl = `/products/${product.handle}`;

    productCard.innerHTML = `
      <div class="product-card-unified__image">
        <a href="${productUrl}">
          ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy">` : ''}
        </a>
        ${this.shouldShowSaleBadge(product) ? '<span class="product-card-unified__badge product-card-unified__badge--sale">Sale</span>' : ''}
        ${!product.available ? '<span class="product-card-unified__badge product-card-unified__badge--sold-out">Sold Out</span>' : ''}
      </div>
      <div class="product-card-unified__content">
        <h3 class="product-card-unified__title">
          <a href="${productUrl}" title="${product.title}">${product.title}</a>
        </h3>
        <div class="product-card-unified__price">
          ${comparePrice ?
            `<span class="product-card-unified__price-sale">${formattedPrice}</span>
             <span class="product-card-unified__price-compare">${comparePrice}</span>` :
            `<span class="product-card-unified__price-regular">${formattedPrice}</span>`
          }
        </div>
        <div class="product-card-unified__actions">
          ${product.available ?
            `<a href="${productUrl}" class="btn btn--primary">View Details</a>` :
            `<button class="btn btn--outline" disabled>Sold Out</button>`
          }
        </div>
      </div>
      <script type="application/json" class="product-data">
        ${JSON.stringify(product)}
      </script>
    `;

    return productCard;
  }

  shouldShowSaleBadge(product) {
    // Check if product has sale or on-sale tags
    const saleTags = ['sale', 'on-sale'];
    const hasSaleTag = product.tags && product.tags.some(tag =>
      saleTags.includes(tag.toLowerCase())
    );

    // Only show badge if product has sale tag AND has compare_at_price > price
    return hasSaleTag && product.compare_at_price && product.compare_at_price > product.price;
  }

  async loadProductsFromDOM() {
    console.log('Loading all products for regular collection search');

    try {
      // Store original DOM state BEFORE making any changes
      this.originalDOMProducts = this.extractCurrentPageProducts();
      this.originalGridHTML = this.productGrid ? this.productGrid.innerHTML : '';
      this.originalResultsText = this.getOriginalResultsText();

      // Get collection handle from URL
      const pathParts = window.location.pathname.split('/');
      const collectionHandle = pathParts[pathParts.indexOf('collections') + 1];

      if (!collectionHandle) {
        console.error('Could not determine collection handle');
        return;
      }

      // Load all products from API
      await this.loadAllProductsFromAPI(collectionHandle);

      console.log(`Loaded ${this.allProducts.length} total products for search`);

    } catch (error) {
      console.error('Failed to load products from API, falling back to DOM:', error);
      this.loadProductsFromDOMFallback();
    }
  }

  getOriginalResultsText() {
    const resultsElement = document.querySelector('.collection-results-count, .results-count');
    return resultsElement ? resultsElement.textContent : '';
  }

  async loadAllProductsFromAPI(collectionHandle) {
    this.allProducts = [];

    // Fetch all products using pagination
    let page = 1;
    let hasMoreProducts = true;

    while (hasMoreProducts) {
      const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250&page=${page}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.products.length === 0) {
        hasMoreProducts = false;
      } else {
        // Convert API products to our format, filtering out unwanted products
        data.products.forEach(product => {
          const productData = this.convertAPIProductToData(product);
          // Filter out products with missing images or "Optional" in the title
          if (this.shouldSkipProduct(productData)) {
            return; // Skip this product
          }
          this.allProducts.push(productData);
        });

        // If we got less than 250 products, we're on the last page
        if (data.products.length < 250) {
          hasMoreProducts = false;
        } else {
          page++;
        }
      }
    }

    // Set filtered products to all products initially
    this.filteredProducts = [...this.allProducts];
  }

  extractCurrentPageProducts() {
    const productCards = document.querySelectorAll('.product-card-unified, .product-card, [data-product-id]');
    const currentPageProducts = [];

    productCards.forEach(card => {
      try {
        const productData = this.extractProductDataFromDOM(card);
        if (productData) {
          // Filter out products with "(Optional)" in the title - these are hidden Bold Product Options products
          if (productData.title && productData.title.includes('(Optional)')) {
            return; // Skip this product
          }
          productData.element = card;
          currentPageProducts.push(productData);
        }
      } catch (error) {
        console.warn('Failed to extract product data from card:', error);
      }
    });

    return currentPageProducts;
  }

  loadProductsFromDOMFallback() {
    console.log('Fallback: Loading products from current page DOM only');

    // Store original state if not already stored
    if (!this.originalDOMProducts) {
      this.originalDOMProducts = this.extractCurrentPageProducts();
      this.originalGridHTML = this.productGrid ? this.productGrid.innerHTML : '';
      this.originalResultsText = this.getOriginalResultsText();
    }

    const productCards = document.querySelectorAll('.product-card-unified, .product-card, [data-product-id]');
    this.allProducts = [];

    productCards.forEach(card => {
      try {
        const productData = this.extractProductDataFromDOM(card);
        if (productData) {
          // Filter out products with missing images or "Optional" in the title
          if (this.shouldSkipProduct(productData)) {
            return; // Skip this product
          }
          productData.element = card;
          this.allProducts.push(productData);
        }
      } catch (error) {
        console.warn('Failed to extract product data from card:', error);
      }
    });

    this.filteredProducts = [...this.allProducts];
    console.log(`Fallback: Loaded ${this.allProducts.length} products from current page`);
  }

  extractProductDataFromDOM(card) {
    // Try to get product ID
    const productId = card.getAttribute('data-product-id') ||
                     card.querySelector('[data-product-id]')?.getAttribute('data-product-id');

    // Get product title
    const titleElement = card.querySelector('.product-card-unified__title a, .product-title a, h3 a, .product-card__title a');
    const title = titleElement?.textContent?.trim() || '';

    // Get product handle from URL
    const linkElement = card.querySelector('a[href*="/products/"]');
    const handle = linkElement?.getAttribute('href')?.split('/products/')[1]?.split('?')[0] || '';

    // Get price
    const priceElement = card.querySelector('.product-card-unified__price .price, .product-price, .price');
    const priceText = priceElement?.textContent?.replace(/[^0-9.]/g, '') || '0';
    const price = Math.round(parseFloat(priceText) * 100) || 0;

    // Get vendor if available
    const vendorElement = card.querySelector('.product-vendor, .vendor');
    const vendor = vendorElement?.textContent?.trim() || '';

    // Get product type if available
    const typeElement = card.querySelector('.product-type');
    const product_type = typeElement?.textContent?.trim() || '';

    // Extract tags from data attributes or classes
    const tags = [];
    const classList = Array.from(card.classList);
    classList.forEach(className => {
      if (className.startsWith('tag-')) {
        tags.push(className.replace('tag-', ''));
      }
    });

    // Extract images from the product card
    const images = [];
    const imageElements = card.querySelectorAll('img[src], img[data-src]');
    imageElements.forEach(img => {
      const src = img.getAttribute('src') || img.getAttribute('data-src');
      if (src && !src.includes('placeholder') && !src.includes('no-image')) {
        images.push({ src });
      }
    });

    if (!title) {
      console.warn('Could not extract title from product card');
      return null;
    }

    return {
      id: productId || Date.now() + Math.random(), // Fallback ID
      title,
      handle,
      price,
      compare_at_price: null,
      available: !card.classList.contains('sold-out'),
      tags,
      images,
      vendor,
      product_type,
      element: card
    };
  }

  buildFilterUI() {
    if (!this.taxonomyData || !this.taxonomyData.filtering || !this.taxonomyData.filtering.enabled) {
      return;
    }

    const filterHTML = this.generateFilterHTML();
    this.container.innerHTML = filterHTML;
  }

  generateFilterHTML() {
    // Dynamic title based on collection or taxonomy data
    const filterTitle = this.getFilterTitle();
    let html = `<div class="filter-header"><h3>${filterTitle}</h3></div>`;

    // Check if we have taxonomy data first
    if (this.taxonomyData && this.taxonomyData.taxonomies) {
      return this.generateTaxonomyFilterHTML(html);
    }

    // Fall back to original logic for non-taxonomy collections
    const currentCollection = this.getCurrentCollectionHandle();
    const availableFilters = this.getAvailableFilters(currentCollection);

    availableFilters.forEach(filter => {
      html += this.generateFilterSection(filter);
    });

    html += '<div class="filter-actions">';
    html += '<button class="clear-filters-btn">Clear All</button>';
    html += '</div>';

    return html;
  }

  getFilterTitle() {
    // Always use "Filter Results" for all collections
    return 'Filter Results';
  }

  generateTaxonomyFilterHTML(headerHtml) {
    let html = headerHtml;

    // Generate taxonomy-based filters
    this.taxonomyData.taxonomies.forEach(taxonomy => {
      html += `<div class="collection-filter-section">`;
      html += `<h4 class="collection-filter-header">${taxonomy.name}</h4>`;
      html += `<div class="collection-filter-list">`;

      taxonomy.collections.forEach(item => {
        html += `
          <label class="collection-filter-item">
            <input type="checkbox"
                   class="collection-filter-checkbox"
                   value="${item.handle}"
                   data-taxonomy="${taxonomy.name.toLowerCase().replace(/\s+/g, '-')}"
                   data-filter-value="${item.title}">
            <span>${item.title}</span>
          </label>
        `;
      });

      html += `</div></div>`;
    });

    html += '<div class="filter-actions">';
    html += '<button class="clear-filters-btn clear-all-filters">Clear All</button>';
    html += '</div>';

    return html;
  }

  generateFilterSection(filter) {
    const options = this.getFilterOptions(filter.type);
    if (options.length === 0) return '';

    let html = `<div class="filter-section" data-filter-type="${filter.type}">`;
    html += `<h4 class="filter-title">${filter.title}</h4>`;
    html += '<div class="filter-options">';

    options.forEach(option => {
      const isActive = this.activeFilters.has(filter.type) && 
                      this.activeFilters.get(filter.type).includes(option.value);
      
      html += `
        <label class="filter-option ${isActive ? 'active' : ''}">
          <input type="checkbox" 
                 data-filter-type="${filter.type}" 
                 data-filter-value="${option.value}"
                 ${isActive ? 'checked' : ''}>
          <span class="filter-label">${option.label}</span>
          <span class="filter-count">(${option.count})</span>
        </label>
      `;
    });

    html += '</div></div>';
    return html;
  }

  getAvailableFilters(collectionHandle) {
    const filters = [];
    
    if (this.taxonomyData && this.taxonomyData.taxonomies) {
      this.taxonomyData.taxonomies.forEach(taxonomy => {
        const collection = taxonomy.collections.find(c => c.handle === collectionHandle);
        if (collection && collection.filters) {
          Object.keys(collection.filters).forEach(filterType => {
            if (!filters.find(f => f.type === filterType)) {
              filters.push({
                type: filterType,
                title: this.formatFilterTitle(filterType),
                options: collection.filters[filterType]
              });
            }
          });
        }
      });
    }

    return filters;
  }

  getFilterOptions(filterType) {
    const options = [];
    const tagPrefix = this.taxonomyData?.filtering?.tag_mapping?.[filterType] || `${filterType}-`;

    // Count products for each filter option
    const optionCounts = new Map();
    
    this.allProducts.forEach(product => {
      if (product.tags) {
        product.tags.forEach(tag => {
          if (tag.startsWith(tagPrefix)) {
            const value = tag.replace(tagPrefix, '');
            optionCounts.set(value, (optionCounts.get(value) || 0) + 1);
          }
        });
      }
    });

    // Convert to options array
    optionCounts.forEach((count, value) => {
      options.push({
        value: value,
        label: this.formatOptionLabel(value),
        count: count
      });
    });

    // Sort by label
    options.sort((a, b) => a.label.localeCompare(b.label));
    
    return options;
  }

  formatFilterTitle(filterType) {
    return filterType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  formatOptionLabel(value) {
    return value.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  getCurrentCollectionHandle() {
    // Extract from URL or page data
    const pathParts = window.location.pathname.split('/');
    const collectionsIndex = pathParts.indexOf('collections');

    if (collectionsIndex !== -1 && pathParts[collectionsIndex + 1]) {
      return pathParts[collectionsIndex + 1];
    }

    return null;
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.filteredProducts.length / this.productsPerPage);

    console.log(`Pagination: ${this.filteredProducts.length} products, ${this.productsPerPage} per page = ${this.totalPages} total pages`);

    // Ensure current page is valid
    if (this.currentPage > this.totalPages) {
      this.currentPage = Math.max(1, this.totalPages);
    }

    this.renderPaginationControls();
  }

  renderPaginationControls() {
    const paginationContainer = document.querySelector('.collection-pagination');
    if (!paginationContainer) return;

    if (this.totalPages <= 1) {
      paginationContainer.style.display = 'none';
      return;
    }

    paginationContainer.style.display = 'block';

    const paginationList = paginationContainer.querySelector('.pagination-list');
    if (!paginationList) return;

    // Clear existing pagination
    paginationList.innerHTML = '';

    // Previous button
    if (this.currentPage > 1) {
      const prevItem = this.createPaginationItem('Previous', this.currentPage - 1, 'pagination-prev');
      paginationList.appendChild(prevItem);
    }

    // Page numbers with improved logic
    this.renderPageNumbers(paginationList);

    // Next button
    if (this.currentPage < this.totalPages) {
      const nextItem = this.createPaginationItem('Next', this.currentPage + 1, 'pagination-next');
      paginationList.appendChild(nextItem);
    }

    // Update pagination info
    const paginationInfo = paginationContainer.querySelector('.pagination-info .pagination-summary');
    if (paginationInfo) {
      const startItem = (this.currentPage - 1) * this.productsPerPage + 1;
      const endItem = Math.min(this.currentPage * this.productsPerPage, this.filteredProducts.length);
      paginationInfo.textContent = `Showing ${startItem}-${endItem} of ${this.filteredProducts.length} products`;
    }
  }

  renderPageNumbers(paginationList) {
    const current = this.currentPage;
    const total = this.totalPages;

    console.log(`Rendering pages for current: ${current}, total: ${total}`);

    // Always show first page
    if (total >= 1) {
      const pageItem = this.createPaginationItem('1', 1, 'pagination-number', current === 1);
      paginationList.appendChild(pageItem);
    }

    // Show ellipsis if there's a gap after page 1
    if (current > 4) {
      const ellipsis = this.createEllipsisItem();
      paginationList.appendChild(ellipsis);
    }

    // Show pages around current page
    const startPage = Math.max(2, current - 1);
    const endPage = Math.min(total - 1, current + 1);

    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== total) { // Don't duplicate first/last pages
        const pageItem = this.createPaginationItem(i.toString(), i, 'pagination-number', i === current);
        paginationList.appendChild(pageItem);
      }
    }

    // Show ellipsis if there's a gap before last page
    if (current < total - 3) {
      const ellipsis = this.createEllipsisItem();
      paginationList.appendChild(ellipsis);
    }

    // Always show last page (if different from first)
    if (total > 1) {
      const pageItem = this.createPaginationItem(total.toString(), total, 'pagination-number', current === total);
      paginationList.appendChild(pageItem);
    }
  }

  createEllipsisItem() {
    const li = document.createElement('li');
    li.className = 'pagination-item';
    const span = document.createElement('span');
    span.className = 'pagination-link pagination-ellipsis';
    span.textContent = '...';
    span.setAttribute('aria-hidden', 'true');
    li.appendChild(span);
    return li;
  }

  createPaginationItem(text, page, className, isCurrent = false) {
    const li = document.createElement('li');
    li.className = 'pagination-item';

    if (isCurrent) {
      const span = document.createElement('span');
      span.className = `pagination-link ${className} pagination-current`;
      span.textContent = text;
      span.setAttribute('aria-current', 'page');
      li.appendChild(span);
    } else {
      const a = document.createElement('a');
      a.href = '#';
      a.className = `pagination-link ${className}`;
      a.textContent = text;
      a.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToPage(page);
      });
      li.appendChild(a);
    }

    return li;
  }

  goToPage(page) {
    this.currentPage = page;
    this.updateProductDisplay();
    this.updatePagination();
    this.updateURL();

    // Scroll to top of products
    const productsContainer = document.querySelector('.collection-products');
    if (productsContainer) {
      productsContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  bindEvents() {
    // Filter checkbox changes (both old and new systems)
    this.container.addEventListener('change', (e) => {
      console.log('=== CHECKBOX CHANGE EVENT ===');
      console.log('Target:', e.target);
      console.log('Target type:', e.target.type);
      console.log('Target classes:', e.target.className);
      console.log('Target checked:', e.target.checked);

      if (e.target.type === 'checkbox') {
        if (e.target.classList.contains('collection-filter-checkbox')) {
          console.log('Calling handleTaxonomyFilterChange');
          // Handle taxonomy-based filtering
          this.handleTaxonomyFilterChange(e.target);
        } else {
          console.log('Calling handleFilterChange');
          // Handle original tag-based filtering
          this.handleFilterChange(e.target);
        }
      }
    });

    // Clear filters button (both classes)
    this.container.addEventListener('click', (e) => {
      if (e.target.classList.contains('clear-filters-btn') || e.target.classList.contains('clear-all-filters')) {
        this.clearAllFilters();
      }
    });

    // Browser back/forward
    window.addEventListener('popstate', () => {
      this.parseURLFilters();
      this.applyFilters();
    });
  }

  handleFilterChange(checkbox) {
    const filterType = checkbox.dataset.filterType;
    const filterValue = checkbox.dataset.filterValue;

    if (!this.activeFilters.has(filterType)) {
      this.activeFilters.set(filterType, []);
    }

    const values = this.activeFilters.get(filterType);

    if (checkbox.checked) {
      if (!values.includes(filterValue)) {
        values.push(filterValue);
      }
    } else {
      const index = values.indexOf(filterValue);
      if (index > -1) {
        values.splice(index, 1);
      }
    }

    // Remove empty filter types
    if (values.length === 0) {
      this.activeFilters.delete(filterType);
    }

    this.applyFilters();
    this.updateURL();
    this.dispatchFilterEvent();
    this.updateClearButtonVisibility();
  }

  handleTaxonomyFilterChange(checkbox) {
    console.log(`Filter: ${checkbox.dataset.filterValue} (${checkbox.checked ? 'ON' : 'OFF'})`);

    // Get the taxonomy type and display value
    const taxonomyType = checkbox.dataset.taxonomy;
    const displayValue = checkbox.dataset.filterValue;

    // Look up the actual tag from the smart filtering tag mapping
    const tagMapping = this.taxonomyData?.smart_filtering?.tag_mapping || this.taxonomyData?.filtering?.tag_mapping;

    // Convert hyphenated taxonomy type to underscore format for lookup
    const taxonomyKey = taxonomyType.replace(/-/g, '_');

    if (!tagMapping || !tagMapping[taxonomyKey]) {
      console.warn('No tag mapping found for taxonomy key:', taxonomyKey);
      return;
    }

    // Find the matching tag for this display value
    // For now, we'll use a simple mapping approach
    let actualTag = null;

    if (taxonomyKey === 'machine_type') {
      const machineTypeMap = {
        'Skid Steers': 'skid-steer',
        'Excavators': 'excavator',
        'Tractors': 'tractor',
        'Wheel Loaders': 'wheel-loader',
        'Dozers': 'dozer',
        'Loader Backhoes': 'loader-backhoe',
        'Off-Highway Trucks': 'off-highway-truck'
      };
      actualTag = machineTypeMap[displayValue];
    } else if (taxonomyKey === 'work_type') {
      const workTypeMap = {
        'Land Clearing': 'land-clearing',
        'Farm & Ranch': 'farm-and-ranch',
        'Concrete & Demolition': 'concrete-and-demolition',
        'Material Handling': 'material-handling',
        'Excavation': 'excavation',
        'Auger (Boring)': 'auger',
        'Snow Removal': 'snow-removal'
      };
      actualTag = workTypeMap[displayValue];
    } else if (taxonomyKey === 'brand') {
      const brandMap = {
        'Eterra': 'eterra',
        'Digga': 'digga',
        'Haugen': 'haugen',
        'ECHO': 'echo',
        'MNKota': 'mnkota'
      };
      actualTag = brandMap[displayValue];
    } else if (taxonomyKey === 'material') {
      const materialMap = {
        'Polycarbonate': 'polycarbonate',
        'Tempered Glass': 'glass'
      };
      actualTag = materialMap[displayValue];
    } else if (taxonomyKey === 'make') {
      // For makes, convert display value to lowercase
      actualTag = displayValue.toLowerCase().replace(/\s+/g, '-');
    } else if (taxonomyKey === 'make') {
      const makeMap = {
        'ASV': 'asv',
        'Bobcat': 'bobcat',
        'Case': 'case',
        'Caterpillar': 'caterpillar',
        'Gehl': 'gehl',
        'Hitachi': 'hitachi',
        'Hyundai': 'hyundai',
        'JCB': 'jcb',
        'John Deere': 'john-deere',
        'Kobelco': 'kobelco',
        'Komatsu': 'komatsu',
        'Kubota': 'kubota',
        'New Holland': 'new-holland',
        'Takeuchi': 'takeuchi',
        'Terex': 'terex',
        'Wacker Neuson': 'wacker-neuson',
        'Yanmar': 'yanmar'
      };
      actualTag = makeMap[displayValue];
    } else if (taxonomyKey === 'part_type') {
      const partTypeMap = {
        'Final Drives': 'final-drive',
        'Hydraulic Cylinders': 'hydraulic-cylinder',
        'Cab Components': 'cab-component',
        'Swing Drives': 'swing-drive',
        'Undercarriage Parts': 'undercarriage',
        'Hydraulic Pumps': 'hydraulic-pump',
        'Engine Parts': 'engine',
        'Electrical Parts': 'electrical',
        'Fuel System': 'fuel-system'
      };
      actualTag = partTypeMap[displayValue];
    }

    if (!actualTag) {
      console.warn('Could not map display value to tag:', displayValue);
      return;
    }

    console.log('Mapped display value:', displayValue, 'to tag:', actualTag);

    // Use the taxonomy type as the filter type, and the actual tag as the value
    if (!this.activeFilters.has(taxonomyType)) {
      this.activeFilters.set(taxonomyType, []);
    }

    const values = this.activeFilters.get(taxonomyType);

    if (checkbox.checked) {
      if (!values.includes(actualTag)) {
        values.push(actualTag);
      }
    } else {
      const index = values.indexOf(actualTag);
      if (index > -1) {
        values.splice(index, 1);
      }
    }

    // Remove empty filter types
    if (values.length === 0) {
      this.activeFilters.delete(taxonomyType);
    }

    console.log('Active filters after change:', this.activeFilters);

    // Use the existing tag-based filtering system
    this.applyFilters();
    this.updateURL();
    this.dispatchFilterEvent();
    this.updateClearButtonVisibility();
    this.updateBreadcrumbs();
  }

  updateClearButtonVisibility() {
    const clearButtons = this.container.querySelectorAll('.clear-filters-btn, .clear-all-filters');
    const hasActiveFilters = this.activeFilters.size > 0;

    clearButtons.forEach(btn => {
      btn.style.display = hasActiveFilters ? 'inline-block' : 'none';
    });
  }



  parseURLFilters() {
    this.activeFilters.clear();
    
    const urlParams = new URLSearchParams(window.location.search);
    
    urlParams.forEach((value, key) => {
      if (key.startsWith('filter_')) {
        const filterType = key.replace('filter_', '');
        const values = value.split(',').filter(v => v.trim() !== '');
        
        if (values.length > 0) {
          this.activeFilters.set(filterType, values);
        }
      }
    });
  }

  parseURLPage() {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');

    if (pageParam) {
      const page = parseInt(pageParam, 10);
      if (page > 0) {
        this.currentPage = page;
      }
    }

    console.log('Parsed URL page:', this.currentPage);
  }

  applyFilters(resetPage = true) {
    // Apply both taxonomy filters and search
    this.filteredProducts = this.allProducts.filter(product => {
      // Check taxonomy filters
      const matchesFilters = this.activeFilters.size === 0 || this.productMatchesFilters(product);

      // Check search query
      const matchesSearch = this.searchProduct(product, this.searchQuery);

      return matchesFilters && matchesSearch;
    });

    const searchInfo = this.searchQuery ? ` (search: "${this.searchQuery}")` : '';
    console.log(`Filtered: ${this.filteredProducts.length}/${this.allProducts.length} products visible${searchInfo}`);

    // Only reset to page 1 when filters change, not on initial load
    if (resetPage) {
      this.currentPage = 1;
    }

    this.updatePagination();
    this.updateProductDisplay();
    this.updateResultsCount();
    this.updateBreadcrumbs();
    this.updateClearButtonVisibility();
  }

  productMatchesFilters(product) {
    for (const [filterType, values] of this.activeFilters) {
      // For taxonomy filters, the values ARE the actual tags (no prefix needed)
      const hasMatchingTag = values.some(expectedTag => {
        return product.tags && product.tags.includes(expectedTag);
      });

      if (!hasMatchingTag) {
        return false;
      }
    }

    return true;
  }

  updateProductDisplay() {
    // Calculate pagination
    const startIndex = (this.currentPage - 1) * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;
    const productsToShow = this.filteredProducts.slice(startIndex, endIndex);

    console.log(`Showing page ${this.currentPage}: products ${startIndex + 1}-${Math.min(endIndex, this.filteredProducts.length)} of ${this.filteredProducts.length}`);

    // Create a document fragment to hold the reordered products
    const fragment = document.createDocumentFragment();

    // Use the stored element references to reorder products
    productsToShow.forEach((filteredProduct, index) => {
      // Use the stored element reference if available
      if (filteredProduct.element) {
        fragment.appendChild(filteredProduct.element);
      } else {
        console.warn(`No element reference for product: ${filteredProduct.title}`);
      }
    });

    // Clear the grid and append the reordered products
    this.productGrid.innerHTML = '';
    this.productGrid.appendChild(fragment);
  }



  updateResultsCount() {
    if (this.resultsCount) {
      const count = this.filteredProducts.length;
      const total = this.allProducts.length;
      
      this.resultsCount.textContent = `Showing ${count} of ${total} products`;
    }
  }

  updateBreadcrumbs() {
    // Find breadcrumbs container (multiple possible locations)
    const breadcrumbsContainer = this.breadcrumbs || document.querySelector('.filter-breadcrumbs') || document.querySelector('.collection-breadcrumbs');

    if (!breadcrumbsContainer) {
      return;
    }

    if (this.activeFilters.size === 0) {
      breadcrumbsContainer.innerHTML = '';
      return;
    }

    let breadcrumbHTML = '<span class="breadcrumb-item">Filtered by:</span>';

    this.activeFilters.forEach((values, filterType) => {
      const filterTitle = this.formatFilterTitle(filterType);
      const valueLabels = values.map(v => this.formatOptionLabel(v)).join(', ');

      breadcrumbHTML += `
        <span class="breadcrumb-filter filter-breadcrumb">
          ${filterTitle}: ${valueLabels}
          <button class="remove-filter" data-filter-type="${filterType}">×</button>
        </span>
      `;
    });

    breadcrumbsContainer.innerHTML = breadcrumbHTML;
  }

  updateURL() {
    const url = new URL(window.location);

    // Clear existing filter params and page param
    const keysToDelete = [];
    for (const key of url.searchParams.keys()) {
      if (key.startsWith('filter_') || key === 'page') {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => {
      url.searchParams.delete(key);
    });

    // Add current filters
    this.activeFilters.forEach((values, filterType) => {
      if (values.length > 0) {
        url.searchParams.set(`filter_${filterType}`, values.join(','));
      }
    });

    // Add page parameter if not page 1
    if (this.currentPage > 1) {
      url.searchParams.set('page', this.currentPage.toString());
    }

    // Update URL without page reload
    window.history.pushState({}, '', url.toString());
  }

  clearAllFilters() {
    this.activeFilters.clear();
    this.currentPage = 1; // Reset to page 1

    // Uncheck all checkboxes
    const checkboxes = document.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        checkbox.checked = false;
      }
    });

    // Hide clear button
    const clearButtons = document.querySelectorAll('.clear-filters-btn, .clear-all-filters');
    clearButtons.forEach(btn => {
      btn.style.display = 'none';
    });

    // Update mobile filter count badge
    const filterCountBadge = document.getElementById('mobile-filter-count');
    if (filterCountBadge) {
      filterCountBadge.style.display = 'none';
      filterCountBadge.textContent = '';
    }

    this.applyFilters();
    this.updateURL();
    this.dispatchFilterEvent();
  }

  dispatchFilterEvent() {
    // Dispatch custom event for SEO utility and other listeners
    const event = new CustomEvent('filtersChanged', {
      detail: {
        filters: this.activeFilters,
        filteredCount: this.filteredProducts.length,
        totalCount: this.allProducts.length
      }
    });

    document.dispatchEvent(event);
  }

  setupTaxonomySorting() {
    const sortSelect = document.querySelector('select[name="sort_by"], .sort-select, #sort-by');
    if (sortSelect) {
      console.log('Setting up client-side sorting for taxonomy collection');

      // Set initial value from URL
      const urlParams = new URLSearchParams(window.location.search);
      const currentSort = urlParams.get('sort_by');
      if (currentSort) {
        sortSelect.value = currentSort;
      }

      // Replace with client-side sorting
      const newSortSelect = sortSelect.cloneNode(true);
      sortSelect.parentNode.replaceChild(newSortSelect, sortSelect);

      // Restore value after cloning
      if (currentSort) {
        newSortSelect.value = currentSort;
      }

      // Add client-side sort behavior
      newSortSelect.addEventListener('change', (e) => {
        this.applySorting(e.target.value);
        this.updateSortURL(e.target.value);
      });
    }
  }

  applySorting(sortValue) {
    console.log('Applying sort:', sortValue);

    // For server-side sorts (featured, best-selling, newest), we need to reload products with correct sort
    if (['manual', 'best-selling', 'created-descending'].includes(sortValue)) {
      console.log('Applying server-side sort:', sortValue);
      this.loadProductsWithSort(sortValue);
      return;
    }

    // For client-side sorts, work with current filtered products
    const sortedProducts = [...this.filteredProducts];



    switch (sortValue) {
      case 'price-ascending':
        sortedProducts.sort((a, b) => {
          const priceA = parseFloat(a.price) || 0;
          const priceB = parseFloat(b.price) || 0;
          return priceA - priceB;
        });
        break;
      case 'price-descending':
        sortedProducts.sort((a, b) => {
          const priceA = parseFloat(a.price) || 0;
          const priceB = parseFloat(b.price) || 0;
          return priceB - priceA;
        });
        break;
      case 'title-ascending':
        sortedProducts.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'title-descending':
        sortedProducts.sort((a, b) => b.title.localeCompare(a.title));
        break;
      case 'created-ascending':
        sortedProducts.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      default:
        // Keep current order for any unhandled cases
        break;
    }



    // Update the filtered products with sorted order
    this.filteredProducts = sortedProducts;

    // Reset to page 1 when sorting changes
    this.currentPage = 1;

    // Update pagination and display with sorted products
    this.updatePagination();
    this.updateProductDisplay();
    this.updateResultsCount();
  }



  updateSortURL(sortValue) {
    const url = new URL(window.location);

    if (sortValue === 'manual' || sortValue === 'featured' || sortValue === '') {
      url.searchParams.delete('sort_by');
    } else {
      url.searchParams.set('sort_by', sortValue);
    }

    // Update URL without page reload
    window.history.pushState({}, '', url.toString());
  }

  buildCrossReferences() {
    const crossRefsContainer = document.querySelector('.collection-cross-refs');
    if (!crossRefsContainer || !this.taxonomyData) {
      return;
    }

    // Get current collection handle to find cross-references
    const currentCollection = this.getCurrentCollectionHandle();
    let crossRefs = null;
    let currentCollectionTitle = '';

    // Find the current collection in the taxonomy data to get its cross-references
    this.taxonomyData.taxonomies.forEach(taxonomy => {
      taxonomy.collections.forEach(collection => {
        if (collection.handle === currentCollection) {
          crossRefs = collection.cross_refs;
          currentCollectionTitle = collection.title;
        }
      });
    });

    if (!crossRefs) {
      // For any taxonomy-enabled collection without specific cross-refs, show all taxonomy navigation
      this.buildDynamicTaxonomyCrossRefs(crossRefsContainer, currentCollection);
      return;
    }

    // Build cross-reference HTML
    let crossRefsHTML = `
      <h3>Related Collections</h3>
      <p class="cross-refs-description">Explore related ${currentCollectionTitle.toLowerCase()} options:</p>
    `;

    // Add machine type cross-references
    if (crossRefs.machine_type && crossRefs.machine_type.length > 0) {
      crossRefsHTML += `
        <div class="cross-refs-section">
          <h4>By Machine Type</h4>
          <div class="cross-refs-links">
      `;

      crossRefs.machine_type.forEach(handle => {
        const collection = this.findCollectionByHandle(handle);
        if (collection) {
          crossRefsHTML += `
            <a href="/collections/${handle}" class="cross-ref-link">
              ${collection.title}
            </a>
          `;
        }
      });

      crossRefsHTML += `</div></div>`;
    }

    // Add popular brands cross-references
    if (crossRefs.popular_brands && crossRefs.popular_brands.length > 0) {
      crossRefsHTML += `
        <div class="cross-refs-section">
          <h4>Popular Brands</h4>
          <div class="cross-refs-links">
      `;

      crossRefs.popular_brands.forEach(brandHandle => {
        const collection = this.findCollectionByHandle(`${brandHandle}-attachments`);
        if (collection) {
          crossRefsHTML += `
            <a href="/collections/${brandHandle}-attachments" class="cross-ref-link">
              ${collection.title}
            </a>
          `;
        }
      });

      crossRefsHTML += `</div></div>`;
    }

    crossRefsContainer.innerHTML = crossRefsHTML;
    crossRefsContainer.style.display = 'block';
  }

  findCollectionByHandle(handle) {
    if (!this.taxonomyData) return null;

    for (const taxonomy of this.taxonomyData.taxonomies) {
      for (const collection of taxonomy.collections) {
        if (collection.handle === handle) {
          return collection;
        }
      }
    }
    return null;
  }

  buildDynamicTaxonomyCrossRefs(crossRefsContainer, currentCollection) {
    // Determine collection type for description
    let collectionType = 'products';
    if (currentCollection.includes('attachment')) {
      collectionType = 'attachments';
    } else if (currentCollection.includes('door') || currentCollection.includes('window')) {
      collectionType = 'doors & windows';
    }

    // Build dynamic cross-references for all taxonomies
    let crossRefsHTML = `
      <h3>Browse by Category</h3>
      <p class="cross-refs-description">Find ${collectionType} by category:</p>
    `;

    // Dynamically add all taxonomies from the schema
    this.taxonomyData.taxonomies.forEach(taxonomy => {
      if (taxonomy.collections && taxonomy.collections.length > 0) {
        // Convert handle to display name (e.g., 'work-type' -> 'Work Type')
        const displayName = taxonomy.name || taxonomy.handle
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        crossRefsHTML += `
          <div class="cross-refs-section">
            <h4>By ${displayName}</h4>
            <div class="cross-refs-links">
        `;

        taxonomy.collections.forEach(collection => {
          crossRefsHTML += `
            <a href="/collections/${collection.handle}" class="cross-ref-link">
              ${collection.title}
            </a>
          `;
        });

        crossRefsHTML += `</div></div>`;
      }
    });

    crossRefsContainer.innerHTML = crossRefsHTML;
    crossRefsContainer.style.display = 'block';
  }

  async loadProductsWithSort(sortValue) {
    try {
      console.log('Loading products with server-side sort:', sortValue);

      // Map our sort values to Shopify's sort_by parameters
      const sortMapping = {
        'manual': 'manual',
        'best-selling': 'best-selling',
        'created-descending': 'created-descending',
        'created-ascending': 'created-ascending',
        'price-ascending': 'price-ascending',
        'price-descending': 'price-descending',
        'title-ascending': 'title-ascending',
        'title-descending': 'title-descending'
      };

      const shopifySortBy = sortMapping[sortValue] || 'manual';
      const collectionHandle = window.location.pathname.split('/collections/')[1]?.split('/')[0];

      if (!collectionHandle) {
        console.error('Could not determine collection handle');
        return;
      }

      // Clear existing products
      this.allProducts = [];

      // Fetch all products with the specified sort
      let page = 1;
      let hasMoreProducts = true;

      while (hasMoreProducts) {
        const url = `/collections/${collectionHandle}/products.json?limit=250&page=${page}&sort_by=${shopifySortBy}`;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.products && data.products.length > 0) {
          // Convert API products to our format, filtering out unwanted products
          data.products.forEach(apiProduct => {
            const productData = this.convertAPIProductToData(apiProduct);
            // Filter out products with missing images or "Optional" in the title
            if (this.shouldSkipProduct(productData)) {
              return; // Skip this product
            }
            this.allProducts.push(productData);
          });

          page++;
          hasMoreProducts = data.products.length === 250; // Continue if we got a full page
        } else {
          hasMoreProducts = false;
        }
      }

      console.log(`Loaded ${this.allProducts.length} products with sort: ${sortValue}`);

      // Apply client-side sorting for created-descending since Shopify API doesn't respect sort_by parameter
      if (sortValue === 'created-descending') {
        this.allProducts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      }

      // Re-render all products with new sort order
      this.renderAllProducts();

      // Re-apply current filters
      this.applyFilters(false);

      // Reset to page 1
      this.currentPage = 1;

      // Update display
      this.updatePagination();
      this.updateProductDisplay();
      this.updateResultsCount();

    } catch (error) {
      console.error('Failed to load products with sort:', error);
      // Fallback to current products if API fails
      this.applyFilters(false);
      this.currentPage = 1;
      this.updatePagination();
      this.updateProductDisplay();
      this.updateResultsCount();
    }
  }

  // ===== SEARCH FUNCTIONALITY =====

  initializeSearch() {
    this.searchInput = document.getElementById('collection-search');
    this.searchClearBtn = document.getElementById('search-clear');

    if (!this.searchInput) {
      console.log('Search input not found, skipping search initialization');
      return;
    }

    console.log('Initializing collection search functionality');

    // Bind search events
    this.searchInput.addEventListener('input', (e) => {
      this.handleSearchInput(e.target.value);
    });

    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.clearSearch();
      }
    });

    if (this.searchClearBtn) {
      this.searchClearBtn.addEventListener('click', () => {
        this.clearSearch();
      });
    }
  }

  handleSearchInput(query) {
    // Clear previous debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Debounce search for performance
    this.searchDebounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  performSearch(query) {
    this.searchQuery = query.trim().toLowerCase();

    // Show/hide clear button
    if (this.searchClearBtn) {
      this.searchClearBtn.style.display = this.searchQuery ? 'block' : 'none';
    }

    console.log('Performing search for:', this.searchQuery);

    // Check if this is a taxonomy collection or regular collection
    if (this.taxonomyData && this.taxonomyData.filtering && this.taxonomyData.filtering.enabled) {
      // Taxonomy collection - use existing filtering system
      this.applyFilters(true); // Reset to page 1
      this.updateResultsCount();
    } else {
      // Regular collection - filter DOM elements directly
      this.filterDOMElements();
    }
  }

  clearSearch() {
    this.searchQuery = '';
    this.searchInput.value = '';

    if (this.searchClearBtn) {
      this.searchClearBtn.style.display = 'none';
    }

    console.log('Search cleared');

    // Check if this is a taxonomy collection or regular collection
    if (this.taxonomyData && this.taxonomyData.filtering && this.taxonomyData.filtering.enabled) {
      // Taxonomy collection - use existing filtering system
      this.applyFilters(true);
      this.updateResultsCount();
    } else {
      // Regular collection - show all DOM elements
      this.showAllDOMElements();
    }

    // Focus back to search input
    this.searchInput.focus();
  }

  searchProduct(product, query) {
    if (!query) return true;

    const searchableText = [
      product.title,
      product.vendor || '',
      product.product_type || '',
      ...(product.tags || [])
    ].join(' ').toLowerCase();

    // Simple fuzzy search - check if all query words are found
    const queryWords = query.split(/\s+/).filter(word => word.length > 0);

    return queryWords.every(word => {
      // Exact match or partial match
      return searchableText.includes(word) ||
             this.fuzzyMatch(searchableText, word);
    });
  }

  fuzzyMatch(text, word) {
    // Simple fuzzy matching for typos (allows 1 character difference for words > 3 chars)
    if (word.length <= 3) return false;

    const words = text.split(/\s+/);
    return words.some(textWord => {
      if (textWord.length < word.length - 1 || textWord.length > word.length + 1) {
        return false;
      }

      let differences = 0;
      const maxLength = Math.max(textWord.length, word.length);

      for (let i = 0; i < maxLength; i++) {
        if (textWord[i] !== word[i]) {
          differences++;
          if (differences > 1) return false;
        }
      }

      return differences <= 1;
    });
  }

  filterDOMElements() {
    if (!this.allProducts || this.allProducts.length === 0) {
      console.warn('No products loaded for DOM filtering');
      return;
    }

    // Filter products that match search
    const matchingProducts = this.allProducts.filter(product =>
      this.searchProduct(product, this.searchQuery)
    );

    console.log(`DOM search: ${matchingProducts.length}/${this.allProducts.length} products match`);

    // Clear the product grid
    if (this.productGrid) {
      this.productGrid.innerHTML = '';

      // Add matching products to grid
      matchingProducts.forEach(product => {
        // Create element if it doesn't exist or reuse existing one
        if (!product.element) {
          product.element = this.createProductElement(product);
        }
        this.productGrid.appendChild(product.element);
      });
    }

    // Update results count
    const resultsElement = document.querySelector('.collection-results-count, .results-count');
    if (resultsElement) {
      resultsElement.textContent = `Showing ${matchingProducts.length} of ${this.allProducts.length} products`;
    }

    // Hide pagination controls during search
    this.hidePaginationControls();
  }

  showAllDOMElements() {
    console.log('Restoring original page state');

    if (!this.productGrid) {
      console.warn('No product grid found for restoration');
      return;
    }

    // Restore original grid HTML if we have it
    if (this.originalGridHTML) {
      this.productGrid.innerHTML = this.originalGridHTML;
      console.log('Restored original grid HTML');
    } else if (this.originalDOMProducts && this.originalDOMProducts.length > 0) {
      // Fallback: restore using stored product elements
      this.productGrid.innerHTML = '';
      this.originalDOMProducts.forEach(product => {
        if (product.element) {
          this.productGrid.appendChild(product.element);
        }
      });
      console.log(`Restored ${this.originalDOMProducts.length} original products`);
    } else {
      console.warn('No original state to restore, reloading page');
      window.location.reload();
      return;
    }

    // Restore original results text
    const resultsElement = document.querySelector('.collection-results-count, .results-count');
    if (resultsElement && this.originalResultsText) {
      resultsElement.textContent = this.originalResultsText;
      console.log('Restored original results text:', this.originalResultsText);
    }

    // Show pagination controls again
    this.showPaginationControls();
  }

  hidePaginationControls() {
    const paginationElements = document.querySelectorAll('.pagination, .collection-pagination, [class*="pagination"]');
    paginationElements.forEach(element => {
      element.style.display = 'none';
    });
    console.log('Hidden pagination controls during search');
  }

  showPaginationControls() {
    const paginationElements = document.querySelectorAll('.pagination, .collection-pagination, [class*="pagination"]');
    paginationElements.forEach(element => {
      element.style.display = '';
    });
    console.log('Restored pagination controls');
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('=== COLLECTION FILTERS SCRIPT LOADED ===');
  try {
    window.collectionFilters = new CollectionFilters();
    console.log('CollectionFilters instance created successfully');
  } catch (error) {
    console.error('Error creating CollectionFilters:', error);
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CollectionFilters;
}
